/* build-tag: p2-ladder-19-oct-2026 */
/* ============================================================
   Upakarna - Luxury Cart Drawer logic
   Vanilla JS. Uses Dawn's native Cart AJAX API + sections param
   + cart-drawer.renderContents().
   P2 (19 Oct 2026): this file is the REMOVE half of the gift pair
   (assets/upk-gift-autoadd.js adds). It no longer patches
   window.fetch: assets/upk-cart-events.js is the one interceptor and
   raises upk:cart:add / upk:cart:change, which this file listens to.
   The ladder steps and the qualifying total come from #UpCartConfig
   (snippets/cart-drawer.liquid) and window.UpkCart.
   ============================================================ */
(function () {
  'use strict';

  // Re-read fresh each time instead of caching once at script load: the
  // #UpCartConfig element gets replaced with fresh values (including
  // stackProductIds, which depends on what's currently in the cart) every
  // time the drawer section re-renders after a mutation - a one-time
  // cached read would silently go stale for that field on every add/remove
  // after the very first page load.
  function getConfig() {
    if (window.UpkCart) return window.UpkCart.getConfig();
    try {
      return JSON.parse(document.getElementById('UpCartConfig').textContent);
    } catch (e) {
      return { gifts: [], recsEnabled: true, excludeProductIds: [], giftboxProductId: '' };
    }
  }

  var ROUTES = (window.routes || {});
  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  function GIFTBOX_PID_GET() { return String(getConfig().giftboxProductId || ''); }
  var syncing = false;
  var bundleSyncing = false;
  var discountSyncing = false;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function getDrawer() { return document.querySelector('cart-drawer'); }

  /* Scrolls the horizontally-scrolling milestone row so the current tier
     (the last one with class is-on) is brought into view.
     Two problems compounded here:
     1) This used to live only as an inline <script> in cart-drawer.liquid,
        which ran once on a full page load and never again - any re-render
        (add/remove/quantity-change) left the strip parked wherever it was.
     2) The row uses CSS scroll-snap (scroll-snap-type: x mandatory,
        scroll-snap-align: center per card, see up-cart-drawer.css) to keep
        cards clean-aligned under user swipes. scrollIntoView() fights with
        that on many mobile browsers - the snap engine intercepts the
        smooth-scroll animation and it stalls partway rather than landing on
        the target, which is exactly the "stuck showing the first few cards"
        symptom. Computing the destination ourselves and setting scrollLeft/
        scrollTo directly sidesteps that fight: the destination already
        matches where the target's own snap-align: center would land it, so
        the snap engine has nothing left to correct.
     The per-row key (row.__upkMilestoneKey, P13a.5) de-dupes so unrelated re-renders (e.g. a coupon
     message updating) don't yank the strip back to the same spot the
     customer may have scrolled away from manually. But a quantity change
     (or any update that replaces the whole .up-progress block, e.g. via
     replaceWith in refreshCartDrawerItems) swaps in a BRAND NEW row
     element that always starts at scrollLeft 0 - if the current tier
     hadn't changed, the key alone matched the old value and this function
     returned early, leaving that fresh element sitting unscrolled at the
     start ("snaps back to the first milestone"). Tracking the row element
     itself alongside the key fixes this: a new DOM node always forces a
     fresh scroll regardless of whether the tier text matches. */
  /* P13a.5 (rendered QA, cart at ₹6,198 stuck on steps 1 to 3): the cart page renders its own ladder row
     (snippets/upk-cart-ladder-page) next to the drawer's, and querySelector only ever found the drawer's, so the page row
     never moved. Every row on the page is scrolled now, each with its own key (stored on the element, so a replaced row
     always scrolls); forceMilestoneScroll re-centres all of them on the next drawer open. */
  var forceMilestoneScroll = false;
  function scrollMilestonesIntoView() {
    var rows = document.querySelectorAll('[data-up-milestones]');
    if (!rows.length) return;
    var force = forceMilestoneScroll;
    forceMilestoneScroll = false;
    Array.prototype.forEach.call(rows, function (row) { scrollOneMilestoneRow(row, force); });
  }
  function scrollOneMilestoneRow(row, force) {
    /* P4b.1: the row scrolls so the NEXT step to unlock is in view (the first tile that is not on; the last tile once
       everything is unlocked), on open and after every cart change. */
    var tiles = row.querySelectorAll('.up-milestone');
    var target = row.querySelector('.up-milestone:not(.is-on)') || (tiles.length ? tiles[tiles.length - 1] : null);
    if (!target) return;
    var amtEl = target.querySelector('.up-milestone__amt');
    var key = (amtEl ? amtEl.textContent : '') + '#' + String(Array.prototype.indexOf.call(tiles, target));
    if (!force && key === row.__upkMilestoneKey) return;
    row.__upkMilestoneKey = key;

    var targetCenter = target.offsetLeft + target.offsetWidth / 2;
    var destination = targetCenter - row.clientWidth / 2;
    var maxScroll = row.scrollWidth - row.clientWidth;
    if (destination < 0) destination = 0;
    if (destination > maxScroll) destination = maxScroll;

    if (typeof row.scrollTo === 'function') {
      row.scrollTo({ left: destination, behavior: 'smooth' });
    } else {
      row.scrollLeft = destination;
    }
  }

  function applyRender(state) {
    var drawer = getDrawer();
    if (drawer && state && state.sections && typeof drawer.renderContents === 'function') {
      drawer.renderContents(state);
      // Dawn toggles is-empty only from its own quantity handler; a gift line removed by syncGifts can be the last line, so keep the flag in step here.
      if (typeof state.item_count === 'number') drawer.classList.toggle('is-empty', state.item_count === 0);
      requestAnimationFrame(function () {
        requestAnimationFrame(scrollMilestonesIntoView);
      });
    }
  }

  function cartMutate(url, body) {
    body.sections = 'cart-drawer,cart-icon-bubble';
    body.sections_url = window.location.pathname;
    return fetch(url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) })
      .then(function (r) { return r.json(); });
  }

  // Shared, short-window cached /cart.js read. Several independent triggers
  // (this file's cartUpdate subscriber, its cart-icon-bubble click handler
  // and assets/upk-cart-guard.js, which listens to the same cart events) can
  // all want a fresh cart snapshot within the same tick, right after one
  // mutation; previously each fired its own /cart.js request. The cache lives on
  // `window` (not a module-local var) specifically so upk-cart-guard.js's
  // copy of this exact function shares state with this one: whichever file's
  // copy runs first within the window wins, and the other reuses its
  // in-flight/just-resolved promise instead of firing a second request.
  // 300ms is short enough that no caller ever acts on truly stale data (it's
  // well under human-perceptible latency and every caller already awaits a
  // promise), it just collapses near-simultaneous reads into one.
  function upkFetchCartFresh() {
    var now = Date.now();
    if (window.__upkCartCachePromise && (now - (window.__upkCartCacheTime || 0)) < 300) {
      return window.__upkCartCachePromise;
    }
    window.__upkCartCacheTime = now;
    window.__upkCartCachePromise = fetch((ROUTES.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); });
    return window.__upkCartCachePromise;
  }

  function getCart() {
    return upkFetchCartFresh();
  }

  // The ladder total, the same rule as snippets/upk-ladder-qualifying (P2.2,
  // counting rule of P6b.1): every paid line counts, letters and charms
  // included; not the theme's gift lines, the handles and product ids Liquid
  // printed to #UpCartConfig (the gift box, the gift products, the Theme
  // settings list) or a line priced at 0. One definition, in
  // assets/upk-cart-events.js, shared with upk-gift-autoadd.js so add and
  // remove never disagree; the reduce below is only the fallback when that
  // file has not loaded.
  function qualifyingTotal(cart, config) {
    if (window.UpkCart) return window.UpkCart.qualifying(cart, config);
    var before = !config || config.countBeforeDiscount !== false;
    return cart.items.reduce(function (sum, item) {
      var isGift = item.properties && (item.properties._gift === 'true' || item.properties._gift === true);
      if (isGift || !(item.original_line_price > 0)) return sum;
      return sum + (before ? item.original_line_price : item.final_line_price);
    }, 0);
  }

  /* Auto-add/remove free-gift line items based on the qualifying subtotal.
     Rewritten to be self-healing: on every run it (a) removes any gift
     variant whose threshold is no longer met, (b) collapses duplicate
     lines of the SAME gift variant down to a single quantity-1 line (this
     is what was leaving "Jewellery Cleaning Cloth x4" etc. behind - repeat
     syncGifts() calls from different triggers each adding one more before
     the previous add had landed), and (c) adds any gift that just became
     eligible. Called redundantly from several triggers below so removal
     reliably fires the moment an item's quantity drops or is removed,
     not just on Dawn's own cartUpdate pub/sub (which isn't guaranteed to
     fire for every quantity/remove interaction across all builders).
     Returns the same promise it always did, so callers (including the
     checkout guard below) can await a definitive, settled result. */
  /* P2 (19 Oct 2026): this sync only REMOVES a gift whose step is no longer met
     (also when a code lowers the total) and collapses duplicate gift lines to one
     unit, keyed by product. Adding is assets/upk-gift-autoadd.js. Both read the
     gifts (resolved by handle in Liquid) from #UpCartConfig. */
  function syncGifts() {
    var config = getConfig();
    var gifts = (config.gifts || []).filter(function (g) { return g.productId; });
    if (!gifts.length || syncing) return Promise.resolve();
    syncing = true;

    return getCart().then(function (cart) {
      var total = qualifyingTotal(cart, config);
      var allowed = {};
      /* P13b (Priyal's decision 2, 20 Oct 2026): with a ladder-blocking code on the cart (VAULT70) nothing is allowed, so
         every gift line goes; the rule lives in assets/upk-cart-events.js (window.UpkCart.blocked), the fallback mirrors it. */
      var blocked = window.UpkCart && window.UpkCart.blocked ? window.UpkCart.blocked(cart, config)
        : ((cart.discount_codes || []).some(function (d) { return d && d.code && d.applicable !== false && (config.blockCodes || ['VAULT70']).map(function (c) { return String(c).toUpperCase(); }).indexOf(String(d.code).toUpperCase()) !== -1; }));
      if (!blocked) gifts.forEach(function (g) {
        if (total >= g.thresholdPaise) allowed[String(g.productId)] = true;
      });

      var byProduct = {};
      cart.items.forEach(function (item) {
        var isGift = item.properties && (item.properties._gift === 'true' || item.properties._gift === true);
        if (!isGift) return;
        var pid = String(item.product_id);
        if (!byProduct[pid]) byProduct[pid] = [];
        byProduct[pid].push(item);
      });

      var toRemove = [];
      var toFix = [];
      Object.keys(byProduct).forEach(function (pid) {
        var lines = byProduct[pid];
        if (!allowed[pid]) {
          lines.forEach(function (line) { toRemove.push(line.key); });
          return;
        }
        lines.forEach(function (line, idx) {
          if (idx > 0) { toRemove.push(line.key); return; }
          if (line.quantity !== 1) toFix.push(line.key);
        });
      });

      if (!toRemove.length && !toFix.length) { syncing = false; return; }

      var chain = Promise.resolve();
      toRemove.forEach(function (key) {
        chain = chain.then(function () { return cartMutate((ROUTES.cart_change_url || '/cart/change') + '.js', { id: key, quantity: 0 }); });
      });
      toFix.forEach(function (key) {
        chain = chain.then(function () { return cartMutate((ROUTES.cart_change_url || '/cart/change') + '.js', { id: key, quantity: 1 }); });
      });
      return chain.then(function (last) { applyRender(last); }).then(function () { syncing = false; })
        .catch(function () { syncing = false; });
    }).catch(function () { syncing = false; });
  }

  function syncBundleIntegrity() {
    if (bundleSyncing) return Promise.resolve();
    bundleSyncing = true;

    return getCart().then(function (cart) {
      var groups = {};
      cart.items.forEach(function (item) {
        var bid = item.properties && item.properties['_upk_bundle_id'];
        if (!bid) return;
        if (!groups[bid]) groups[bid] = { keys: [], size: null };
        groups[bid].keys.push(item.key);
        var sz = item.properties['_upk_bundle_size'];
        if (sz && !groups[bid].size) groups[bid].size = parseInt(sz, 10);
      });

      var toRemove = [];
      Object.keys(groups).forEach(function (bid) {
        var g = groups[bid];
        if (g.size && g.keys.length > 0 && g.keys.length < g.size) {
          toRemove = toRemove.concat(g.keys);
        }
      });

      if (!toRemove.length) { bundleSyncing = false; return null; }

      var chain = Promise.resolve(null);
      toRemove.forEach(function (key) {
        chain = chain.then(function () { return cartMutate((ROUTES.cart_change_url || '/cart/change') + '.js', { id: key, quantity: 0 }); });
      });
      return chain.then(function (last) { if (last) applyRender(last); return last; })
        .then(function (last) { bundleSyncing = false; return last; })
        .catch(function () { bundleSyncing = false; });
    }).catch(function () { bundleSyncing = false; });
  }

  function giftBoxKeys(cart) {
    return cart.items.filter(function (i) { return String(i.product_id) === GIFTBOX_PID_GET(); }).map(function (i) { return i.key; });
  }
  function removeGiftBox() {
    return getCart().then(function (cart) {
      var keys = giftBoxKeys(cart);
      if (!keys.length) return null;
      var chain = Promise.resolve(null);
      keys.forEach(function (k) {
        chain = chain.then(function () { return cartMutate((ROUTES.cart_change_url || '/cart/change') + '.js', { id: k, quantity: 0 }); });
      });
      return chain;
    });
  }
  function addGiftBox(vid) {
    return removeGiftBox().then(function () {
      return cartMutate((ROUTES.cart_add_url || '/cart/add') + '.js', { items: [{ id: Number(vid), quantity: 1 }] });
    }).then(function (state) { applyRender(state); });
  }
  function onGiftBoxChange(e) {
    var toggle = e.target.closest && e.target.closest('[data-up-giftbox]');
    if (toggle) {
      var body = $('.up-giftbox__body');
      var sel = $('[data-up-giftbox-select]');
      if (toggle.checked) {
        if (body) body.hidden = false;
        if (sel && sel.value) addGiftBox(sel.value);
      } else {
        if (body) body.hidden = true;
        removeGiftBox().then(function (state) { if (state) applyRender(state); });
      }
      return;
    }
    var select = e.target.closest && e.target.closest('[data-up-giftbox-select]');
    if (select) {
      if (select.value) addGiftBox(select.value);
      else removeGiftBox().then(function (state) { if (state) applyRender(state); });
    }
  }

  function onRecAdd(e) {
    var btn = e.target.closest('[data-up-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;
    var card = btn.closest('.up-rec');
    var sel = card && card.querySelector('[data-up-variant]');
    var vid = (sel && sel.value) ? sel.value : btn.getAttribute('data-up-add');
    if (!vid) return;
    btn.setAttribute('aria-busy', 'true');
    var original = btn.textContent;
    btn.textContent = 'Adding...';
    cartMutate((ROUTES.cart_add_url || '/cart/add') + '.js', {
      items: [{ id: Number(vid), quantity: 1 }]
    }).then(function (state) {
      applyRender(state);
      return syncGifts();
    }).catch(function () {
      btn.removeAttribute('aria-busy');
      btn.textContent = original;
    });
  }

  function launchConfetti() {
    var drawerInner = $('.cart-drawer .drawer__inner') || $('.cart-drawer');
    if (!drawerInner) return;
    var rect = drawerInner.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var canvas = document.createElement('canvas');
    canvas.className = 'up-confetti-canvas';
    canvas.style.position = 'fixed';
    canvas.style.top = rect.top + 'px';
    canvas.style.left = rect.left + 'px';
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '99999';
    document.body.appendChild(canvas);

    var dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    /* P1: brand tokens only. A canvas cannot read var(), so the six values are resolved from the token file at runtime. */
    var tokenStyle = getComputedStyle(document.documentElement);
    var tokenColour = function (name) { return tokenStyle.getPropertyValue(name).trim(); };
    var colors = ['--upk-rust', '--upk-rust', '--upk-green', '--upk-purple', '--upk-mint', '--upk-maroon', '--upk-rust'].map(tokenColour).filter(Boolean);
    var particles = [];
    var count = Math.max(90, Math.min(220, Math.round((rect.width * rect.height) / 2400)));
    for (var i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * rect.width,
        y: -40 - Math.random() * rect.height * 0.9,
        w: 5 + Math.random() * 5,
        h: 8 + Math.random() * 7,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 10,
        vy: 2.2 + Math.random() * 2.6,
        driftAmp: 10 + Math.random() * 18,
        driftFreq: 0.6 + Math.random() * 0.8,
        driftPhase: Math.random() * Math.PI * 2,
        baseX: 0,
        shape: Math.random() > 0.5 ? 'rect' : 'circle',
        opacity: 1
      });
    }
    particles.forEach(function (p) { p.baseX = p.x; });

    var start = null;
    var duration = 2600;
    function frame(ts) {
      if (!start) start = ts;
      var elapsed = ts - start;
      ctx.clearRect(0, 0, rect.width, rect.height);
      particles.forEach(function (p) {
        p.y += p.vy;
        p.x = p.baseX + Math.sin((elapsed / 1000) * p.driftFreq + p.driftPhase) * p.driftAmp;
        p.rotation += p.rotSpeed;
        if (elapsed > duration * 0.65) {
          p.opacity = Math.max(0, 1 - (elapsed - duration * 0.65) / (duration * 0.35));
        }
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      });
      if (elapsed < duration) {
        requestAnimationFrame(frame);
      } else {
        canvas.remove();
      }
    }
    requestAnimationFrame(frame);
  }

  function setCouponMsg(text, cls) {
    var msg = $('.up-couponbar__msg');
    if (!msg) return;
    msg.className = 'up-couponbar__msg up-couponbar__msg--show' + (cls ? (' ' + cls) : '');
    msg.textContent = text;
  }

  function applyCoupon(code) {
    code = (code || '').trim();
    if (!code) return;
    setCouponMsg('Applying...', '');
    cartMutate((ROUTES.cart_update_url || '/cart/update') + '.js', { discount: code })
      .then(function (state) {
        applyRender(state);
        var codes = state.discount_codes || [];
        var matched = codes.filter(function (d) {
          return d.code && d.code.toUpperCase() === code.toUpperCase();
        });
        if (matched.length && matched[0].applicable) {
          launchConfetti();
          setCouponMsg('"' + code + '" applied.', 'up-couponbar__msg--ok');
          setTimeout(function () {
            cartMutate((ROUTES.cart_update_url || '/cart/update') + '.js', {}).then(applyRender);
          }, 350);
          setTimeout(function () {
            cartMutate((ROUTES.cart_update_url || '/cart/update') + '.js', {}).then(applyRender);
          }, 900);
        } else {
          setCouponMsg('"' + code + '" isn\u2019t valid for this cart right now.', 'up-couponbar__msg--err');
        }
      })
      .catch(function () {
        setCouponMsg('Could not apply that code.', 'up-couponbar__msg--err');
      });
  }

  function removeCoupon(silent) {
    if (!silent) setCouponMsg('Removing...', '');
    return cartMutate((ROUTES.cart_update_url || '/cart/update') + '.js', { discount: '' })
      .then(function (state) {
        applyRender(state);
        if (!silent) setCouponMsg('Discount removed.', '');
        /* P13b: the cart page's rows, ladder and offers are server-rendered; a code removed there (the VAULT70 line's Remove) reloads the page the way an Apply does. */
        if (!silent && document.querySelector('cart-items')) setTimeout(function () { window.location.reload(); }, 900);
        return state;
      })
      .catch(function () {
        if (!silent) setCouponMsg('Could not remove the code.', 'up-couponbar__msg--err');
      });
  }

  function syncDiscountValidity() {
    if (discountSyncing) return Promise.resolve();
    discountSyncing = true;
    return getCart().then(function (cart) {
      var codes = cart.discount_codes || [];
      var hasInapplicable = codes.some(function (d) { return d.code && !d.applicable; });
      discountSyncing = false;
      if (hasInapplicable) return removeCoupon(true);
    }).catch(function () { discountSyncing = false; });
  }

  // Offers and codes (decision 46a): one tap applies a listed code; on the cart page the page reloads with it on.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-up-offer-apply]') : null;
    if (!btn) return;
    e.preventDefault();
    var code = btn.getAttribute('data-up-offer-apply') || '';
    if (!code) return;
    btn.disabled = true;
    btn.textContent = 'Applying';
    applyCoupon(code);
    if (document.querySelector('cart-items')) setTimeout(function () { window.location.reload(); }, 1500);
  });

  // Phase 4: one Remove for a built piece removes the base and every part (keys joined by | on the button).
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-up-remove-group]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var keys = (btn.getAttribute('data-up-remove-group') || '').split('|').filter(Boolean);
    if (!keys.length) return;
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    btn.disabled = true;
    fetch(((ROUTES.cart_update_url) || '/cart/update') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) { applyRender(state); })
      .catch(function () { btn.disabled = false; });
  }, true);

  function refreshCartDrawerItems() {
    var url = (ROUTES.cart_url || '/cart') + '?section_id=cart-drawer';
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        /* .up-progress (the milestone rail/dots/cards block) and .up-announce
           (the "Add ₹X more..." message) were missing from this list, so an
           add-to-cart from any builder that doesn't trigger Dawn's own full
           section re-render (Charm Bar, Watch Bar, PDP personalisation,
           sticky ATC, this drawer's own recs, etc.) left the milestone strip
           completely stale - still showing the pre-add tier, with nothing
           for the auto-scroll to even react to. Refreshing them here too
           means every add-to-cart path keeps the milestones in sync, not
           just the ones Dawn's native handlers happen to catch. */
        /* #UpCartConfig sits outside the element Dawn re-renders, so it is refreshed here as well (P4b: it went stale after an add). */
        /* P13b: the whole drawer header is swapped (bar, close button, row, note), so a ladder-blocking code (VAULT70) can turn the ladder
           into its one line and back; the selectors are scoped to the drawer so the cart page's own ladder is never taken for it. */
        ['#UpCartConfig', 'cart-drawer .drawer__header', 'cart-drawer [data-up-codes]', 'cart-drawer cart-drawer-items', 'cart-drawer .cart-drawer__footer'].forEach(function (sel) {
          var target = document.querySelector(sel);
          var source = doc.querySelector(sel);
          if (target && source) target.replaceWith(source);
        });
        /* P2.9: the unlocked-gift cards (.up-gifts) sit between the items and the footer and render only while a
           gift line exists, so they are inserted, replaced or removed here rather than replaced in place. */
        var giftsNow = document.querySelector('.cart-drawer .up-gifts');
        var giftsNew = doc.querySelector('.up-gifts');
        var itemsEl = document.querySelector('.cart-drawer cart-drawer-items');
        if (giftsNow && giftsNew) giftsNow.replaceWith(giftsNew);
        else if (giftsNow && !giftsNew) giftsNow.remove();
        else if (!giftsNow && giftsNew && itemsEl) itemsEl.insertAdjacentElement('afterend', giftsNew);
      })
      .catch(function () {});
  }

  // Coalescing scheduler for refreshCartDrawerItems(). Two independent
  // triggers below (the fetch-patch on /cart/add, and the cartUpdate
  // pub/sub subscriber) each want to re-check the drawer shortly after a
  // mutation, to catch a write that hadn't fully committed yet when the
  // first render came back. Previously each trigger scheduled its own set
  // of timeouts independently (3 + 2 = up to 5 refetches for one add), so
  // a rapid sequence of triggers piled up redundant full-page requests.
  // This single shared scheduler clears any refresh timers a previous
  // trigger in the same burst already queued and re-schedules from now -
  // so a burst collapses to at most 2 actual fetches (covering both a
  // fast-settling and a slow-settling write) rather than stacking one set
  // per trigger. The milestone re-scroll after each refresh is unchanged.
  var refreshTimers = [];
  function scheduleCartRefresh() {
    refreshTimers.forEach(function (t) { clearTimeout(t); });
    refreshTimers = [400, 1200].map(function (delay) {
      return setTimeout(function () {
        refreshCartDrawerItems().then(function () {
          requestAnimationFrame(function () {
            requestAnimationFrame(scrollMilestonesIntoView);
          });
        });
      }, delay);
    });
  }

  /* ------------------------------------------------------------
     Site-wide add-to-cart safety net.
     Multiple independent builders exist across the storefront
     (Charm Bar, Watch Bar, Band+Charm builder, Quick View, sticky
     ATC, PDP personalisation, this drawer's own recs) and not all
     of them reliably publish Dawn's cartUpdate pub/sub event after
     a successful /cart/add. assets/upk-cart-events.js, the ONE fetch
     interceptor (P2.1), raises upk:cart:add after any successful
     /cart/add and upk:cart:change after /cart/change, /cart/update or
     /cart/clear, whoever made the call. The drawer refreshes on the
     first; the gift thresholds are re-checked on the second, so a
     gift is dropped the moment the total that earned it drops below
     the line (a code lowering the total included).
     ------------------------------------------------------------ */
  document.addEventListener('upk:cart:add', function () { scheduleCartRefresh(); });
  document.addEventListener('upk:cart:change', function () {
    setTimeout(syncGifts, 400);
    setTimeout(syncGifts, 1100);
  });

  function onApplyClick(e) {
    var btn = e.target.closest('[data-up-apply]');
    if (!btn) return;
    e.preventDefault();
    applyCoupon(btn.getAttribute('data-up-apply'));
  }
  function onRemoveClick(e) {
    var btn = e.target.closest('[data-up-remove]');
    if (!btn) return;
    e.preventDefault();
    removeCoupon();
  }
  function onCouponApplyClick(e) {
    var btn = e.target.closest('[data-up-coupon-apply]');
    if (!btn) return;
    e.preventDefault();
    var wrap = btn.closest('.up-offers-wrap') || document;
    var inp = wrap.querySelector('.up-couponbar__input');
    if (inp) applyCoupon(inp.value);
  }

  /* ------------------------------------------------------------
     Checkout guard: a free-gift line dropped below its threshold
     right before the customer taps Secure Checkout could still be
     mid-flight (syncGifts runs on a short delay after each quantity
     change, per above) when the click happens - so it was possible
     to reach real Shopify checkout with a stale, no-longer-earned
     gift line still in the cart, or without a gift that was just
     earned. Intercepting the checkout click, waiting for any
     in-flight sync to settle, then running one more definitive
     syncGifts() pass (remove) and UpkGiftAutoAdd.run() pass (add)
     before letting the form submit closes that gap: checkout only
     ever sees a cart that's already been reconciled against the
     current qualifying subtotal. Delegated (not bound once) because
     the button is replaced whenever the drawer re-renders.
     ------------------------------------------------------------ */
  function onCheckoutClick(e) {
    var btn = e.target.closest('#CartDrawer-Checkout');
    if (!btn || btn.disabled) return;
    /* Minimum order (decision 55): the drawer render disables the button below the minimum; this keeps a stale button from submitting. */
    if (btn.getAttribute('data-up-below-min') === 'true') { e.preventDefault(); return; }
    if (btn.__upGiftsChecked) { btn.__upGiftsChecked = false; return; }
    e.preventDefault();

    var settled = 0;
    function proceed() {
      var formEl = document.getElementById('CartDrawer-Form');
      var liveBtn = document.getElementById('CartDrawer-Checkout') || btn;
      if (!formEl) return;
      liveBtn.__upGiftsChecked = true;
      if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit(liveBtn);
      } else {
        formEl.submit();
      }
    }
    function waitThenSync() {
      settled += 1;
      if (syncing && settled < 30) { setTimeout(waitThenSync, 100); return; }
      syncGifts()
        .then(function () { return (window.UpkGiftAutoAdd && window.UpkGiftAutoAdd.run) ? window.UpkGiftAutoAdd.run() : null; })
        .then(proceed, proceed);
    }
    waitThenSync();
  }

  function bind() {
    document.addEventListener('click', function (e) {
      onRecAdd(e);
      onApplyClick(e);
      onRemoveClick(e);
      onCouponApplyClick(e);
      onCheckoutClick(e);
      /* Redundant trigger: quantity +/- buttons and the remove button both
         go through Dawn's own handlers (which call /cart/change.js), and
         the fetch patch above already re-checks gifts after that - this
         extra pass just covers browsers/paths where that patch doesn't
         catch it (e.g. a handler that uses XMLHttpRequest instead of
         fetch for some reason). Cheap no-op if nothing changed. */
      if (e.target.closest('.quantity__button') || e.target.closest('.cart-remove-button')) {
        setTimeout(syncGifts, 600);
        setTimeout(syncGifts, 1400);
      }
    });
    document.addEventListener('change', function (e) {
      onGiftBoxChange(e);
      if (e.target.classList && e.target.classList.contains('quantity__input')) {
        setTimeout(syncGifts, 600);
        setTimeout(syncGifts, 1400);
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.classList && e.target.classList.contains('up-couponbar__input')) {
        e.preventDefault();
        applyCoupon(e.target.value);
      }
    });

    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (payload) {
        if (payload && payload.source === 'up-cart-gifts') return;
        syncGifts();
        syncBundleIntegrity();
        syncDiscountValidity();
        /* Safety net: a cart mutation from elsewhere on the page (Standard
           Actions add-to-cart, quick-add, etc.) fires this event, and Dawn's
           own cart-drawer-items subscriber immediately re-fetches the drawer
           section - but that fetch can land before Shopify's storefront has
           finished writing the new line item, so it comes back showing the
           pre-add cart (same write/read lag as the discount-apply
           timing issue). A short delayed re-fetch catches the settled
           state without disturbing the immediate one. */
        scheduleCartRefresh();
      });
    }
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) bubble.addEventListener('click', function () {
      setTimeout(syncGifts, 50);
      setTimeout(syncBundleIntegrity, 50);
      setTimeout(syncDiscountValidity, 50);
    });

    document.addEventListener('cart:refresh', function () { syncBundleIntegrity(); });

    /* Dawn's own quantity +/- and remove-button handlers re-render the
       drawer through their own native code path (replacing the whole
       <cart-drawer> element's contents), not through applyRender() above -
       so hooking every specific trigger event is fragile and easy to miss
       one. Watching the DOM directly instead: <cart-drawer> itself is a
       persistent custom element that is never removed, only its contents
       get swapped, so a MutationObserver on it reliably catches every
       re-render regardless of which code path caused it, and re-runs the
       milestone auto-scroll after each one settles. */
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl && window.MutationObserver) {
      var scrollDebounce;
      var mo = new MutationObserver(function () {
        clearTimeout(scrollDebounce);
        scrollDebounce = setTimeout(scrollMilestonesIntoView, 80);
      });
      mo.observe(drawerEl, { childList: true, subtree: true });
      /* P4b.1: on every open (Dawn adds the active class) the row re-centres on the next step, even if the key matched. */
      var wasActive = drawerEl.classList.contains('active');
      var moOpen = new MutationObserver(function () {
        var active = drawerEl.classList.contains('active');
        if (active && !wasActive) {
          forceMilestoneScroll = true;
          setTimeout(scrollMilestonesIntoView, 60);
          setTimeout(function () { forceMilestoneScroll = true; scrollMilestonesIntoView(); }, 420);
        }
        wasActive = active;
      });
      moOpen.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
    }
    /* P13a.5: the cart page's own ladder row lives in <cart-items> (sections/main-cart-items), which Dawn's cart.js
       re-renders on every quantity change; watch it the same way so the fresh row scrolls to its next step. */
    var cartPageEl = document.querySelector('cart-items');
    if (cartPageEl && window.MutationObserver) {
      var pageScrollDebounce;
      new MutationObserver(function () {
        clearTimeout(pageScrollDebounce);
        pageScrollDebounce = setTimeout(scrollMilestonesIntoView, 80);
      }).observe(cartPageEl, { childList: true, subtree: true });
    }
  }

  /* Runs the milestone scroll for the initial page load itself, not just
     later re-renders. Called both right away (covers the common case where
     milestone images are already cached/laid out) and again on window
     'load' (covers the rest: lazy-loaded milestone icon images finishing
     after DOMContentLoaded can change each card's offsetLeft, and this
     recomputes against the settled layout). */
  function initMilestoneScroll() {
    scrollMilestonesIntoView();
    window.addEventListener('load', scrollMilestonesIntoView);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(); syncGifts(); syncBundleIntegrity(); initMilestoneScroll(); });
  } else {
    bind();
    syncGifts();
    syncBundleIntegrity();
    initMilestoneScroll();
  }
})();
